import {tx, ptx, usePageText} from '../lib/page-text';
import { useState } from 'react';
import { useNavigate, useParams, Link } from '../lib/localized-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { Modal, Code } from '../components/ui';
import { PlusIcon, KeyIcon } from '../components/icons';

interface FeatureDef { code: string; label: string; description: string; group: string }
interface Project {
  id: string; name: string; slug: string; description: string | null;
  features: string[]; region: string; status: string; createdAt: string;
}
interface ProjectKey {
  id: string; name: string; prefix: string; scopes: string[];
  lastUsedAt: string | null; expiresAt: string | null; revokedAt: string | null; createdAt: string;
}

const SCOPES = [
  { id: 'chat:token',         desc: 'Mint chat tokens for end-users (server-to-server)' },
  { id: 'channels:read',      desc: 'List and read channels and members' },
  { id: 'channels:write',     desc: 'Create / update / delete channels and members' },
  { id: 'streams:read',       desc: 'Read stream metadata and status' },
  { id: 'streams:write',      desc: 'Create / update / delete streams + rotate keys' },
  { id: 'recordings:read',    desc: 'List recordings, fetch playback URLs' },
  { id: 'recordings:write',   desc: 'Trigger recordings, delete VOD' },
  { id: 'webhooks:read',      desc: 'List webhook endpoints + delivery history' },
  { id: 'webhooks:write',     desc: 'Register / delete webhook endpoints' },
];

export function ProjectDetailPage() {
  usePageText();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [revealed, setRevealed] = useState<{ name: string; fullKey: string } | null>(null);

  const project = useQuery<Project>({
    queryKey: ['project', id],
    queryFn: async () => (await api.get(`/me/projects/${id}`)).data,
  });
  const catalog = useQuery<FeatureDef[]>({
    queryKey: ['feature-catalog'],
    queryFn: async () => (await api.get('/me/projects/features/catalog')).data,
  });
  const keys = useQuery<ProjectKey[]>({
    queryKey: ['project-keys', id],
    queryFn: async () => (await api.get(`/me/projects/${id}/keys`)).data,
    enabled: !!id,
  });

  const toggleFeature = useMutation({
    mutationFn: async (next: string[]) => (await api.patch(`/me/projects/${id}/features`, { features: next })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', id] }),
  });

  const revokeKey = useMutation({
    mutationFn: async (keyId: string) => (await api.delete(`/me/projects/${id}/keys/${keyId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-keys', id] }),
  });

  const deleteProject = useMutation({
    mutationFn: async () => (await api.delete(`/me/projects/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      navigate('/projects');
    },
  });

  function toggle(code: string) {
    if (!project.data) return;
    const cur = new Set(project.data.features);
    if (cur.has(code)) cur.delete(code); else cur.add(code);
    toggleFeature.mutate(Array.from(cur));
  }

  if (!project.data || !catalog.data) return <p className="muted">{tx("Loading…")}</p>;

  // group features
  const groups: Record<string, FeatureDef[]> = {};
  for (const f of catalog.data) (groups[f.group] = groups[f.group] || []).push(f);

  return (
    <div>
      <p className="muted" style={{ fontSize: 13, marginBottom: 6 }}>
        <Link to="/projects" style={{ color: 'inherit' }}>{tx("← Projects")}</Link>
      </p>
      <div className="row-between" style={{ marginBottom: 28 }}>
        <div>
          <h1 className="h1">{project.data.name}</h1>
          <p className="subtitle" style={{ marginBottom: 0 }}>
            <span className="mono">{project.data.slug}</span> · {project.data.region}
            {project.data.description && <> · {project.data.description}</>}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="badge">{project.data.status}</span>
          <button
            className="btn outline sm danger"
            disabled={deleteProject.isPending}
            onClick={() => {
              if (confirm(`Delete project "${project.data!.name}"?\n\nThis archives the project. API keys stop working immediately. Channels and messages stored under it are preserved for audit but will not be served.`)) {
                deleteProject.mutate();
              }
            }}
          >
            {deleteProject.isPending ? tx("Deleting…") : tx("Delete project")}
          </button>
        </div>
      </div>

      {/* ─── Features ─────────────────────────────────────────────── */}
      <h2 className="h2" style={{ marginTop: 8, marginBottom: 12 }}>{tx("Features")}</h2>
      <p className="muted" style={{ marginTop: -6, marginBottom: 16, fontSize: 13 }}>{tx("Toggle the capabilities you need. Disabling a feature stops new sessions but does not delete existing data.")}</p>

      {Object.entries(groups).map(([group, items]) => (
        <div key={group} style={{ marginBottom: 16 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{group}</div>
          <div className="card" style={{ padding: '4px 16px' }}>
            {items.map((f) => (
              <div key={f.code} className="toggle-row">
                <div className="label-block">
                  <span className="name">{f.label}</span>
                  <span className="desc">{f.description}</span>
                </div>
                <button
                  type="button"
                  className={`toggle ${project.data!.features.includes(f.code) ? 'on' : ''}`}
                  onClick={() => toggle(f.code)}
                  disabled={toggleFeature.isPending}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* ─── API keys ─────────────────────────────────────────────── */}
      <div className="row-between" style={{ marginTop: 32, marginBottom: 12 }}>
        <div>
          <h2 className="h2" style={{ marginBottom: 4 }}>{tx("API keys")}</h2>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>{tx("Project-scoped. Use these from your server to call the Kardolive API.")}</p>
        </div>
        <button className="btn" onClick={() => setShowCreateKey(true)}>{ptx("{v0} New key", {v0: (<PlusIcon size={14}/>)})}</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {keys.data && keys.data.length === 0
          ? <div style={{ padding: 28, textAlign: 'center' }} className="muted">{tx("No keys yet. Create one to call the API.")}</div>
          : (
            <table>
              <thead><tr><th>{tx("Name")}</th><th>{tx("Prefix")}</th><th>{tx("Scopes")}</th><th>{tx("Created")}</th><th>{tx("Last used")}</th><th></th></tr></thead>
              <tbody>
                {keys.data?.map((k) => (
                  <tr key={k.id}>
                    <td>{k.name}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{ptx("kl_live_{v0}…", {v0: (k.prefix)})}</td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {k.scopes.map((s) => <span key={s} className="badge">{s}</span>)}
                      </div>
                    </td>
                    <td className="muted">{new Date(k.createdAt).toLocaleDateString()}</td>
                    <td className="muted">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : tx("never")}</td>
                    <td>
                      {k.revokedAt
                        ? <span className="badge warn">{tx("revoked")}</span>
                        : <button className="btn outline sm" onClick={() => { if (confirm(`Revoke "${k.name}"? This breaks any client using it.`)) revokeKey.mutate(k.id); }}>{tx("Revoke")}</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <PushCredsSection projectId={id!} />

      <ToolsHub />

      <div className="card" style={{ marginTop: 24, padding: '14px 18px', background: 'rgba(127,127,255,0.06)', borderLeft: '3px solid var(--primary)' }}>
        <div style={{ fontSize: 14, marginBottom: 4 }}>
          <strong>{tx("Integrating?")}</strong>{tx(" The full 3-step recipe (mint token → connect client → webhook) lives in the developer docs.")}</div>
        <a className="btn outline sm" href="/Live/Developer/project-integrate.html" target="_blank" rel="noopener" style={{ marginTop: 6 }}>{tx("Open project integration guide →")}</a>
      </div>

      {showCreateKey && (
        <CreateKeyModal
          projectId={id!}
          onClose={() => setShowCreateKey(false)}
          onCreated={(d) => { setShowCreateKey(false); setRevealed(d); qc.invalidateQueries({ queryKey: ['project-keys', id] }); }}
        />
      )}
      {revealed && <RevealKeyModal name={revealed.name} fullKey={revealed.fullKey} onClose={() => setRevealed(null)}/>}
    </div>
  );
}

// ───────────────── Push credentials ──────────────────────────────
interface PushCredsState {
  fcmEnabled: boolean; fcmConfigured: boolean; fcmProjectId: string | null;
  apnsEnabled: boolean; apnsConfigured: boolean; apnsBundleId: string | null; apnsProduction: boolean;
}

function PushCredsSection({ projectId }: { projectId: string }) {
  usePageText();
  const qc = useQueryClient();
  const [showFcm, setShowFcm] = useState(false);
  const [showApns, setShowApns] = useState(false);
  const creds = useQuery<PushCredsState>({
    queryKey: ['push-creds', projectId],
    queryFn: async () => (await api.get(`/me/push/projects/${projectId}/credentials`)).data,
  });
  const toggleFcm = useMutation({
    mutationFn: async (next: boolean) => (await api.post(`/me/push/projects/${projectId}/credentials/fcm`, { fcmEnabled: next })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['push-creds', projectId] }),
  });
  const toggleApns = useMutation({
    mutationFn: async (next: boolean) => (await api.post(`/me/push/projects/${projectId}/credentials/apns`, { apnsEnabled: next })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['push-creds', projectId] }),
  });
  return (
    <>
      <h2 className="h2" style={{ marginTop: 32, marginBottom: 8 }}>{tx("Push notifications")}</h2>
      <p className="muted" style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>{tx("When a user is offline and gets a DM, @mention, or call invite, we deliver via APNs (native iOS) or FCM (Android + Web + iOS fallback).")}</p>

      {/* APNs */}
      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <div className="row-between" style={{ marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 500 }}>{tx("Apple Push (APNs)")}</div>
            <div className="muted" style={{ fontSize: 12 }}>
              {creds.data?.apnsConfigured
                ? <>{tx("Configured · ")}<span className="mono">{creds.data.apnsBundleId}</span> {creds.data.apnsProduction ? tx("(production)") : tx("(sandbox)")}</>
                : tx("Not configured — upload your .p8 key, team id, and bundle id below.")}
            </div>
          </div>
          <button
            type="button"
            className={`toggle ${creds.data?.apnsEnabled ? 'on' : ''}`}
            disabled={!creds.data?.apnsConfigured || toggleApns.isPending}
            onClick={() => toggleApns.mutate(!creds.data?.apnsEnabled)}
          />
        </div>
        <button className="btn outline sm" onClick={() => setShowApns(true)}>
          {creds.data?.apnsConfigured ? tx("Replace APNs credentials") : tx("Configure APNs")}
        </button>
      </div>

      {/* FCM */}
      <div className="card" style={{ padding: 16 }}>
        <div className="row-between" style={{ marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 500 }}>{tx("Firebase Cloud Messaging")}</div>
            <div className="muted" style={{ fontSize: 12 }}>
              {creds.data?.fcmConfigured
                ? <>{tx("Configured · project ")}<span className="mono">{creds.data.fcmProjectId}</span></>
                : tx("Not configured — upload your Firebase service-account JSON below.")}
            </div>
          </div>
          <button
            type="button"
            className={`toggle ${creds.data?.fcmEnabled ? 'on' : ''}`}
            disabled={!creds.data?.fcmConfigured || toggleFcm.isPending}
            onClick={() => toggleFcm.mutate(!creds.data?.fcmEnabled)}
          />
        </div>
        <button className="btn outline sm" onClick={() => setShowFcm(true)}>
          {creds.data?.fcmConfigured ? tx("Replace service-account JSON") : tx("Upload service-account JSON")}
        </button>
      </div>

      {showFcm && (
        <UploadFcmModal
          projectId={projectId}
          onClose={() => setShowFcm(false)}
          onSaved={() => { setShowFcm(false); qc.invalidateQueries({ queryKey: ['push-creds', projectId] }); }}
        />
      )}
      {showApns && (
        <UploadApnsModal
          projectId={projectId}
          onClose={() => setShowApns(false)}
          onSaved={() => { setShowApns(false); qc.invalidateQueries({ queryKey: ['push-creds', projectId] }); }}
        />
      )}
    </>
  );
}

function UploadApnsModal({ projectId, onClose, onSaved }: { projectId: string; onClose: () => void; onSaved: () => void }) {
  usePageText();
  const [teamId, setTeamId] = useState('');
  const [keyId, setKeyId] = useState('');
  const [bundleId, setBundleId] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [production, setProduction] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () =>
      (await api.post(`/me/push/projects/${projectId}/credentials/apns`, {
        apnsEnabled: true,
        apnsTeamId: teamId.trim(),
        apnsKeyId: keyId.trim(),
        apnsBundleId: bundleId.trim(),
        apnsPrivateKey: privateKey.trim(),
        apnsProduction: production,
      })).data,
    onSuccess: onSaved,
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Save failed'),
  });
  return (
    <Modal onClose={onClose}>
      <h2>{tx("APNs credentials")}</h2>
      <p className="modal-subtitle">{tx("From Apple Developer → Certificates, Identifiers & Profiles → Keys → register an APNs key (.p8). We use token-based auth; one key works for all your apps under the team.")}</p>

      <label className="label">{tx("Team ID")}</label>
      <input className="input" value={teamId} onChange={(e) => setTeamId(e.target.value)} placeholder={tx("e.g. ABCDE12345")}/>

      <div style={{ height: 8 }}/>
      <label className="label">{tx("Key ID")}</label>
      <input className="input" value={keyId} onChange={(e) => setKeyId(e.target.value)} placeholder={tx("e.g. AB12CD34EF")}/>

      <div style={{ height: 8 }}/>
      <label className="label">{tx("Bundle ID")}</label>
      <input className="input" value={bundleId} onChange={(e) => setBundleId(e.target.value)} placeholder={tx("e.g. com.your.app")}/>

      <div style={{ height: 8 }}/>
      <label className="label">{tx("Private key (.p8 contents)")}</label>
      <textarea className="input" rows={6} value={privateKey} onChange={(e) => setPrivateKey(e.target.value)}
        placeholder={"-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----"}
        style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}/>

      <div style={{ height: 10 }}/>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>{ptx("{v0}Use production APNs host (api.push.apple.com)", {v0: (<input type="checkbox" checked={production} onChange={(e) => setProduction(e.target.checked)}/>)})}</label>
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{tx("Leave off during dev (sandbox host). Tokens from TestFlight builds also use sandbox.")}</div>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
      <div className="row-between" style={{ marginTop: 16 }}>
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
        <button className="btn" disabled={!teamId || !keyId || !bundleId || !privateKey || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? tx("Saving…") : tx("Save + enable")}
        </button>
      </div>
    </Modal>
  );
}

function UploadFcmModal({ projectId, onClose, onSaved }: { projectId: string; onClose: () => void; onSaved: () => void }) {
  usePageText();
  const [json, setJson] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => {
      let parsed: unknown;
      try { parsed = JSON.parse(json); } catch { throw new Error('Invalid JSON'); }
      return (await api.post(`/me/push/projects/${projectId}/credentials/fcm`, {
        fcmEnabled: true,
        fcmServiceAccount: parsed,
      })).data;
    },
    onSuccess: onSaved,
    onError: (e: any) => setErr(e?.message ?? 'Save failed'),
  });
  return (
    <Modal onClose={onClose}>
      <h2>{tx("Firebase service account")}</h2>
      <p className="modal-subtitle">{tx("In Firebase Console → Project Settings → Service Accounts → Generate new private key. Paste the entire JSON here. It's stored encrypted on our side and only used to send pushes for this project.")}</p>
      <textarea
        className="input"
        rows={10}
        value={json}
        onChange={(e) => setJson(e.target.value)}
        placeholder={"{ \"type\": \"service_account\", \"project_id\": \"...\", \"private_key\": \"...\" }"}
        style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
      />
      {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
      <div className="row-between" style={{ marginTop: 16 }}>
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
        <button className="btn" disabled={!json || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? tx("Saving…") : tx("Save + enable")}
        </button>
      </div>
    </Modal>
  );
}

// ───────────────── Tools hub ─────────────────────────────────────
// One glance at everything chat gives you. Each row links to docs or the
// dashboard area where you can act on it.
const TOOLS = [
  { group: 'Server APIs (Bearer kl_live_*)', items: [
    { name: 'POST /v1/me/channels',                desc: 'Create direct, group, public, or stream channels' },
    { name: 'POST /v1/me/channels/:id/members',    desc: 'Add or update channel members + roles' },
    { name: 'POST /v1/me/channels/:id/messages',   desc: 'Send a message server-side (threads + mentions + attachments)' },
    { name: 'PATCH /v1/me/channels/:id/messages/:msgId',  desc: 'Edit a message — sets editedAt timestamp' },
    { name: 'DELETE /v1/me/channels/:id/messages/:msgId', desc: 'Redact a message (preserved in audit, hidden from clients)' },
    { name: 'GET /v1/me/channels/:id/messages',    desc: 'Cursor-paginated history with thread + before filters' },
    { name: 'POST /v1/me/channels/:id/read',       desc: 'Mark a member read up to now (drives unread counts)' },
    { name: 'POST /v1/me/channels/token',          desc: 'Mint short-lived chat JWT for an end-user' },
    { name: 'POST /v1/me/channels/uploads/url',    desc: 'Pre-signed S3 URL for chat file/image/voice uploads (max 25 MB)' },
    { name: 'GET /v1/me/channels/analytics/summary', desc: 'Channels, members, message volume — for your dashboard' },
    { name: 'GET /v1/me/channels/search/messages?q=…', desc: 'Postgres full-text search across all messages (websearch_to_tsquery syntax)' },
    { name: 'POST /v1/me/push/devices', desc: 'Register an end-user device token (FCM or APNs)' },
    { name: 'POST /v1/me/push/projects/:id/credentials/fcm', desc: 'Set the project Firebase service account for push delivery' },
  ] },
  { group: 'Sessions (livestream / call / conference / webinar)', items: [
    { name: 'POST /v1/me/sessions',                         desc: 'Create a session — pick type + recording + waiting room + layout + watermark' },
    { name: 'POST /v1/me/sessions/:id/token',               desc: 'Mint a per-user JWT with role + publish perms (audio/video/screen)' },
    { name: 'POST /v1/me/sessions/:id/participants',        desc: 'Invite participants (hosts, cohosts, guests, viewers)' },
    { name: 'PATCH /v1/me/sessions/:id/participants/:userId', desc: 'Change role or publish permissions mid-session' },
    { name: 'POST /v1/me/sessions/:id/moderate/mute-all',   desc: 'Mute everyone except hosts in one call' },
    { name: 'POST /v1/me/sessions/:id/moderate/spotlight',  desc: 'Pin a participant for everyone (layout=spotlight)' },
    { name: 'POST /v1/me/sessions/:id/moderate/hand-raise', desc: 'Audience interaction → host promotion flow' },
    { name: 'POST /v1/me/sessions/:id/moderate/promote',    desc: 'Promote a viewer to cohost (or guest → cohost)' },
    { name: 'POST /v1/me/sessions/:id/recording/start',     desc: 'Start composite recording (grid/speaker/sidebar/composite layout)' },
    { name: 'POST /v1/me/sessions/:id/end',                 desc: 'End the session for everyone + mark recordings processing' },
    { name: 'GET /v1/me/sessions/analytics/summary',        desc: 'Sessions, joins, recordings, by type + state' },
  ] },
  { group: '1:1 calls (voice + video)', items: [
    { name: 'POST /v1/me/calls/invite',                  desc: 'Create call session + push the callee with a high-priority ringing notification' },
    { name: 'POST /v1/me/calls/:sessionId/answer',       desc: 'Callee accepts — session goes live' },
    { name: 'POST /v1/me/calls/:sessionId/decline',      desc: 'Callee declines — session cancelled' },
    { name: 'POST /v1/me/calls/:sessionId/end',          desc: 'Either party ends a live call' },
  ] },
  { group: 'Client WebSocket (wss://chat.kardocloud.com)', items: [
    { name: 'chat.send',          desc: 'Send a message with optional parentId + mentions + attachments' },
    { name: 'chat.edit',          desc: 'Author edits — broadcasts chat.edited to room' },
    { name: 'chat.delete',        desc: 'Delete a message you authored, or any if you have mod role' },
    { name: 'typing.start / typing.stop', desc: 'Ephemeral typing indicators (no persistence)' },
    { name: 'read.mark',          desc: 'Mark channel read up to a timestamp — broadcasts "read" event' },
    { name: 'reaction.add/remove', desc: 'Emoji reactions, per-message or channel-wide' },
    { name: 'poll.create/vote/close', desc: 'Polls with auto-close timer' },
    { name: 'qa.ask/upvote/answer',   desc: 'Q&A queue with upvotes (webinar-style)' },
    { name: 'gift.send + superchat.send', desc: 'Gifts + paid pinned messages with amount + currency' },
    { name: 'mod.timeout / ban / mute / slowmode / bannedwords', desc: 'Moderation actions (host role)' },
  ] },
  { group: 'Webhooks (server-side push)', items: [
    { name: 'chat.message.created', desc: 'Every message — perfect for push notifications and CRM sync' },
    { name: 'chat.message.edited',  desc: 'Author edited their message' },
    { name: 'chat.message.deleted', desc: 'Message redacted by author or moderator' },
    { name: 'channel.member.joined / left', desc: 'Member events for presence systems' },
    { name: 'chat.moderation.action', desc: 'Timeout, ban, mute — for audit pipelines' },
  ] },
  { group: 'Drop-in UI', items: [
    { name: '<script src="/widgets/chat.js">', desc: 'Zero-dependency 6 KB browser widget — paste into any HTML page' },
    { name: '@kardolive/sdk (JS/TS)', desc: 'Typed client for Node, browser, RN; channels + messages + tokens + reactions' },
    { name: 'SDKs in 14 more languages', desc: 'Swift, Kotlin, Java, Python, Go, Rust, Flutter, C#, Unity, Unreal, C++, PHP, Ruby, RN' },
  ] },
] as const;

function ToolsHub() {
  usePageText();
  return (
    <>
      <h2 className="h2" style={{ marginTop: 24, marginBottom: 4 }}>{tx("What you can do")}</h2>
      <p className="muted" style={{ marginTop: 0, marginBottom: 16, fontSize: 13 }}>{tx("Every tool, endpoint, and event Kardolive exposes for this project. Pick whichever surface fits your stack.")}</p>
      {TOOLS.map((section) => (
        <div key={section.group} style={{ marginBottom: 18 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{section.group}</div>
          <div className="card" style={{ padding: 0 }}>
            <table>
              <tbody>
                {section.items.map((t) => (
                  <tr key={t.name}>
                    <td className="mono" style={{ fontSize: 12, whiteSpace: 'nowrap', verticalAlign: 'top', padding: '8px 14px' }}>{t.name}</td>
                    <td className="muted" style={{ fontSize: 13, padding: '8px 14px' }}>{t.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
}

function CreateKeyModal({ projectId, onClose, onCreated }: {
  projectId: string;
  onClose: () => void;
  onCreated: (d: { name: string; fullKey: string }) => void;
}) {
  usePageText();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['chat:token']);
  const [err, setErr] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: async () => (await api.post(`/me/projects/${projectId}/keys`, { name, scopes })).data,
    onSuccess: (data) => onCreated({ name, fullKey: data.fullKey }),
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not create'),
  });
  function toggle(scope: string) {
    setScopes((cur) => cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope]);
  }
  return (
    <Modal onClose={onClose}>
      <h2>{tx("New API key")}</h2>
      <p className="modal-subtitle">{tx("Pick the minimum scopes needed. Keys are shown only once.")}</p>
      <label className="label">{tx("Name")}</label>
      <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={tx("e.g. Kardopal backend")}/>
      <div style={{ height: 18 }}/>
      <label className="label">{tx("Scopes")}</label>
      <div className="card" style={{ padding: '4px 16px', maxHeight: 360, overflow: 'auto' }}>
        {SCOPES.map((s) => (
          <div key={s.id} className="toggle-row">
            <div className="label-block">
              <span className="name mono" style={{ fontFamily: 'ui-monospace, monospace' }}>{s.id}</span>
              <span className="desc">{s.desc}</span>
            </div>
            <button type="button" className={`toggle ${scopes.includes(s.id) ? 'on' : ''}`} onClick={() => toggle(s.id)}/>
          </div>
        ))}
      </div>
      {err && <div className="error">{err}</div>}
      <div className="row-between" style={{ marginTop: 20 }}>
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
        <button className="btn" disabled={!name || scopes.length === 0 || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? tx("Creating…") : tx("Generate key")}
        </button>
      </div>
    </Modal>
  );
}

function RevealKeyModal({ name, fullKey, onClose }: { name: string; fullKey: string; onClose: () => void }) {
  usePageText();
  return (
    <Modal onClose={onClose}>
      <h2>{tx("API key created")}</h2>
      <p className="modal-subtitle">{ptx("{v0} — it will not be shown again.", {v0: (<strong style={{ color: 'var(--warning)' }}>{tx("Save this key now")}</strong>)})}</p>
      <label className="label">{name}</label>
      <Code value={fullKey} large/>
      <div className="row-between" style={{ marginTop: 20 }}>
        <span/>
        <button className="btn" onClick={onClose}>{tx("I've saved it")}</button>
      </div>
    </Modal>
  );
}
